// MEDLINK point 2: the employee's own "Upload medical certificate" action, on their own sick
// leave entry.
//
// ALWAYS AVAILABLE ON A SICK LEAVE, highlighted only when a certificate is required or missing.
// Somebody who wants to send one for a short absence can; somebody who must send one is told so
// clearly, with the date it is due.
//
// THE DECLARATION ID IS SENT WITH THE UPLOAD, which is the whole point of this build: the server
// stores it as a real foreign key, so the certificate belongs to THIS absence and not to whichever
// one a date heuristic happened to pick. Two sick leaves on overlapping dates are now
// distinguishable, which they were not before.
//
// THIS COMPONENT IS FOR THE EMPLOYEE'S OWN RECORD ONLY. It is rendered from the employee's own
// leave list, and every endpoint it calls is enforced server side: the upload refuses a
// declaration belonging to anybody else (403), and the document route applies its own Article 9
// gate before serving a byte. Nothing here is the enforcing check, and it is not written as if it
// were.
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
import { useTranslation } from 'react-i18next';

const API = '/api';

// The statuses the server reports. Only these two are a call to action; `uploaded` and
// `not_required` are quiet states.
const NEEDS_ACTION = ['required', 'missing'];

export default function CertificateUpload({
  declaration,
  employeeId,
  dark = false,
  onUploaded,
  highlightRef,
}) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Only a sick leave can carry a certificate, which is also what the server enforces.
  if (!declaration || declaration.leave_type !== 'sick') return null;

  const status = declaration.certificate_status || 'not_required';
  const hasFile = Boolean(declaration.certificate_document_id);
  const needsAction = NEEDS_ACTION.includes(status);

  const textCol = dark ? '#e8e4d8' : '#1a2744';
  const mutedCol = dark ? '#9ba8c0' : '#667085';
  const tone = status === 'missing'
    ? { fg: '#A63A3A', bg: dark ? 'rgba(166,58,58,0.12)' : '#fdf5f5', bd: '#A63A3A' }
    : { fg: '#92400E', bg: dark ? 'rgba(146,64,14,0.12)' : '#FEF3C7', bd: '#F59E0B' };

  const pick = () => {
    setError('');
    if (fileRef.current) fileRef.current.click();
  };

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Clear the input straight away so choosing the same file twice still fires a change event.
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('document_type', 'medical_certificate');
      // MEDLINK: the real link. Without this the server stores the certificate against the
      // employee with no declaration, which is exactly the state this build exists to end.
      fd.append('leave_declaration_id', declaration.id);
      fd.append('file', file);
      await axios.post(`${API}/documents/upload`, fd);
      if (onUploaded) onUploaded();
    } catch (err) {
      // UPLOADREFUSE: the server's real reason, translated when it carries a reason code.
      setError(refusalMessage(err, currentLang(), 'certificate.uploadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const s = {
    wrap: {
      marginTop: '8px',
      ...(needsAction ? {
        padding: '10px 12px',
        borderRadius: '6px',
        backgroundColor: tone.bg,
        border: `1px solid ${tone.bd}`,
      } : {}),
    },
    message: {
      margin: '0 0 8px', fontSize: '12px', lineHeight: 1.5,
      color: needsAction ? tone.fg : mutedCol, fontWeight: needsAction ? 600 : 400,
    },
    row: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    btn: {
      fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: busy ? 'wait' : 'pointer',
      border: `1px solid ${needsAction ? tone.bd : '#ddd'}`,
      background: 'transparent', color: needsAction ? tone.fg : textCol, fontFamily: 'inherit',
    },
    link: { fontSize: '12px', color: '#1e5fa8', textDecoration: 'underline' },
    error: { margin: '6px 0 0', fontSize: '11px', color: '#A63A3A' },
  };

  // The message says WHAT is needed and BY WHEN. Never why the person is ill: the declaration's
  // notes are not returned to this screen and are not read here.
  const message = () => {
    if (status === 'missing') {
      return t('certificate.missingMessage', { date: declaration.certificate_due_date || '-' });
    }
    if (status === 'required') {
      return t('certificate.requiredMessage', { date: declaration.certificate_due_date || '-' });
    }
    if (status === 'uploaded') return t('certificate.uploadedMessage');
    return t('certificate.optionalMessage');
  };

  return (
    <div style={s.wrap} ref={highlightRef}>
      <p style={s.message}>{message()}</p>
      <div style={s.row}>
        <button type="button" style={s.btn} onClick={pick} disabled={busy}>
          {busy
            ? t('certificate.uploading')
            : (hasFile ? t('certificate.replaceAction') : t('certificate.uploadAction'))}
        </button>
        {/* The employee may VIEW their own certificate. The link goes to the same audited
            document route every other viewer uses; the server decides who may open it. */}
        {hasFile && (
          <a
            href={`${API}/documents/${declaration.certificate_document_id}/view`}
            target="_blank"
            rel="noopener noreferrer"
            style={s.link}
          >
            {t('certificate.viewAction')}
          </a>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          onChange={upload}
          style={{ display: 'none' }}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}
